import { useState, useEffect, useCallback } from 'react'
import { loadConnectAndInitialize } from '@stripe/connect-js'
import { ConnectComponentsProvider, ConnectAccountOnboarding } from '@stripe/react-connect-js'
import { api } from '../api'
import RinaContextCard from '../components/RinaContextCard'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import PageHub from '../components/PageHub'

/**
 * Espace producteur — Paiements en ligne (Stripe Connect, ADR-051).
 *
 * Onboarding EMBARQUE : le KYC s'affiche directement dans le portail via le
 * composant Connect `account_onboarding` (le producteur ne quitte plus le site).
 * Le statut reel (encaissement / versements) est resynchronise cote backend par
 * le webhook account.updated ; ici on lit / rafraichit le cache.
 *
 * Style : DESIGN-GUIDE v1.1 (liquid glass + tokens semantiques, ref
 * docs/manrina-app-complete.jsx).
 */
function ContenuPaiements() {
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState(null)
  const [connectInstance, setConnectInstance] = useState(null)


  // Banniere de retour d'onboarding (cas fallback redirection : ?stripe=done)
  const stripeReturn = new URLSearchParams(window.location.search).get('stripe')

  const loadStatus = useCallback(() => {
    setLoading(true)
    api.producer.getStripeStatus()
      .then(setStatus)
      .catch(() => setStatus(null))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { loadStatus() }, [loadStatus])

  // Initialise le composant Connect embarque (recupere cle publiable + secret).
  const handleActivate = async () => {
    setStarting(true)
    setError(null)
    try {
      const first = await api.producer.createStripeAccountSession()
      let firstUsed = false
      const instance = loadConnectAndInitialize({
        publishableKey: first.publishableKey,
        fetchClientSecret: async () => {
          if (!firstUsed) { firstUsed = true; return first.clientSecret }
          const next = await api.producer.createStripeAccountSession()
          return next.clientSecret
        },
        appearance: {
          variables: {
            colorPrimary: 'rgb(var(--c-primary))',
            colorText: 'rgb(var(--c-green))',
            borderRadius: '14px',
            fontFamily: 'inherit',
          },
        },
      })
      setConnectInstance(instance)
    } catch (e) {
      setError(
        /503|indisponible|configur|publishable/i.test(e?.message || '')
          ? "Les paiements en ligne ne sont pas encore disponibles. Reessaie plus tard."
          : (e?.message || "Impossible de demarrer l'activation.")
      )
    } finally {
      setStarting(false)
    }
  }

  const handleExit = () => {
    setConnectInstance(null)
    loadStatus()
  }

  if (loading) {
    return (
      <div className="text-center pt-10 animate-fade-up">
        <div className="w-8 h-8 mx-auto border-2 border-primary/25 border-t-transparent rounded-full animate-spin" />
        <p className="text-xs mt-3 text-ink/60">Chargement...</p>
      </div>
    )
  }

  const ready = status?.ready
  const hasAccount = status?.hasAccount

  const ChecklistItem = ({ ok, label }) => (
    <div className="flex items-center gap-2">
      <span className={`w-5 h-5 rounded-full flex items-center justify-center text-white text-xs ${ok ? 'bg-emerald-500' : 'bg-ink/15'}`}>
        {ok ? '✓' : '•'}
      </span>
      <span className={`text-sm ${ok ? 'text-ink' : 'text-ink/60'}`}>{label}</span>
    </div>
  )

  return (
    <>
      {/* v7.2.4 : titre dans la bulle RINA — le retour vit dans le header */}
      <RinaContextCard
       
        title="Paiements en ligne"
        message="Activez votre compte de paiement pour recevoir l'argent de vos ventes en ligne. La vérification d'identité est gérée de manière sécurisée par notre partenaire Stripe."
      />

      {stripeReturn === 'done' && !ready && (
        <Card variant="glass" className="p-3 mb-3 animate-fade-up border border-amber-400/40">
          <p className="text-sm text-amber-600">
            Configuration envoyee. La validation de ton compte peut prendre quelques instants.
          </p>
        </Card>
      )}
      {error && (
        <Card variant="glass" className="p-3 mb-3 animate-fade-up border border-red-400/40">
          <p className="text-sm text-red-500">{error}</p>
        </Card>
      )}

      {/* Onboarding embarque actif : le producteur reste sur le portail */}
      {connectInstance ? (
        <Card variant="glass" className="p-4 animate-fade-up">
          <ConnectComponentsProvider connectInstance={connectInstance}>
            <ConnectAccountOnboarding onExit={handleExit} />
          </ConnectComponentsProvider>
          <button
            onClick={handleExit}
            className="w-full mt-3 py-2 text-sm text-ink/60 hover:text-primary transition"
          >
            Fermer
          </button>
        </Card>
      ) : (
        <>
          {ready && (
            <Card variant="glass" className="p-6 text-center animate-fade-up mb-3">
              <div className="w-16 h-16 mx-auto mb-3 rounded-2xl grad-primary flex items-center justify-center">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="font-semibold mb-1 text-ink">Paiements activés</h3>
              <p className="text-sm text-ink/60">Tu peux recevoir les paiements de tes commandes en ligne.</p>
              <button
                onClick={handleActivate}
                disabled={starting}
                className="mt-4 py-2 px-4 text-sm font-semibold text-ink/70 hover:text-primary transition"
              >
                {starting ? 'Ouverture...' : 'Mettre à jour mes infos (IBAN, identité…)'}
              </button>
            </Card>
          )}

          {/* Checklist toujours visible — y compris une fois valide */}
          <Card variant="glass" className="p-5 animate-fade-up">
            <div className="space-y-2">
              <ChecklistItem ok={hasAccount} label="Compte de paiement cree" />
              <ChecklistItem ok={status?.detailsSubmitted} label="Informations transmises" />
              <ChecklistItem ok={status?.chargesEnabled} label="Encaissement autorise" />
              <ChecklistItem ok={status?.payoutsEnabled} label="Versements autorises" />
            </div>
            {!ready && (
              <>
                <Button variant="primary" fullWidth onClick={handleActivate} disabled={starting} className="mt-4">
                  {starting ? 'Ouverture...' : (hasAccount ? 'Continuer la configuration' : 'Activer les paiements')}
                </Button>
                {hasAccount && (
                  <button
                    onClick={loadStatus}
                    className="w-full mt-2 py-2 text-sm text-ink/60 hover:text-primary transition"
                  >
                    Actualiser le statut
                  </button>
                )}
              </>
            )}
          </Card>
        </>
      )}
    </>
  )
}

// La page porte SON layout (S51) : retour et barre declares ici, la route
// d'App.jsx ne fait que router.
export default function PagePaiements() {
  return <PageHub retour="account"><ContenuPaiements /></PageHub>
}

